/**
 * เขียนไฟล์ .xlsx (Office Open XML) ในเบราว์เซอร์โดยไม่พึ่ง library
 *
 * ทำแค่ที่จอคอนโซลต้องใช้: หลาย sheet, แถวหัวตัวหนา, ค่าเป็นข้อความหรือตัวเลข, กำหนดความกว้างคอลัมน์
 * ข้อความเก็บแบบ inlineStr (ไม่ต้องมี sharedStrings) และ zip แบบ stored (ไม่บีบอัด) —
 * ไฟล์ใหญ่กว่าที่ Excel เขียนเองเล็กน้อยแต่เปิดได้ทั้ง Excel / LibreOffice / Google Sheets
 *
 * รหัส (บ้าน/ตาแสง) ต้องส่งเป็น string เสมอ ไม่งั้น 0101043 จะเสียเลข 0 นำหน้า
 */

export type XlsxCell = string | number | null | undefined;

export interface XlsxSheet {
  /** ชื่อ sheet — ตัดอักขระที่ Excel ห้ามและยาวไม่เกิน 31 ตัวให้เอง */
  name: string;
  /** แถวแรกเป็นหัวตาราง (ตัวหนา + ตรึงไว้) */
  header: string[];
  rows: XlsxCell[][];
  /** ความกว้างคอลัมน์ (หน่วยตัวอักษรของ Excel) */
  widths?: number[];
}

export function buildXlsx(sheets: XlsxSheet[]): Blob {
  const enc = new TextEncoder();
  const names = uniqueSheetNames(sheets.map((s) => s.name));
  const files: { path: string; data: Uint8Array }[] = [
    { path: "[Content_Types].xml", data: enc.encode(contentTypes(sheets.length)) },
    { path: "_rels/.rels", data: enc.encode(ROOT_RELS) },
    { path: "xl/workbook.xml", data: enc.encode(workbook(names)) },
    { path: "xl/_rels/workbook.xml.rels", data: enc.encode(workbookRels(sheets.length)) },
    { path: "xl/styles.xml", data: enc.encode(STYLES) },
    ...sheets.map((s, i) => ({ path: `xl/worksheets/sheet${String(i + 1)}.xml`, data: enc.encode(worksheet(s)) })),
  ];
  return new Blob([zipStored(files)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** ดาวน์โหลด Blob เป็นไฟล์ชื่อที่กำหนด */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------- XML parts

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const NS_MAIN = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const NS_REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

const ROOT_RELS =
  XML_HEAD +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  `<Relationship Id="rId1" Type="${NS_REL}/officeDocument" Target="xl/workbook.xml"/>` +
  "</Relationships>";

// style 0 = ปกติ, 1 = หัวตารางตัวหนาพื้นเทา
const STYLES =
  XML_HEAD +
  `<styleSheet xmlns="${NS_MAIN}">` +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FFEEF1F6"/><bgColor indexed="64"/></patternFill></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/></cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  "</styleSheet>";

function contentTypes(n: number): string {
  const sheets = Array.from(
    { length: n },
    (_, i) =>
      `<Override PartName="/xl/worksheets/sheet${String(i + 1)}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
  ).join("");
  return (
    XML_HEAD +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    sheets +
    "</Types>"
  );
}

function workbook(names: string[]): string {
  const sheets = names
    .map((name, i) => `<sheet name="${esc(name)}" sheetId="${String(i + 1)}" r:id="rId${String(i + 1)}"/>`)
    .join("");
  return XML_HEAD + `<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_REL}"><sheets>${sheets}</sheets></workbook>`;
}

function workbookRels(n: number): string {
  const rels = Array.from(
    { length: n },
    (_, i) =>
      `<Relationship Id="rId${String(i + 1)}" Type="${NS_REL}/worksheet" Target="worksheets/sheet${String(i + 1)}.xml"/>`,
  ).join("");
  const styles = `<Relationship Id="rId${String(n + 1)}" Type="${NS_REL}/styles" Target="styles.xml"/>`;
  return (
    XML_HEAD + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${rels}${styles}</Relationships>`
  );
}

function worksheet(sheet: XlsxSheet): string {
  const cols = sheet.widths?.length
    ? "<cols>" +
      sheet.widths
        .map((w, i) => `<col min="${String(i + 1)}" max="${String(i + 1)}" width="${String(w)}" customWidth="1"/>`)
        .join("") +
      "</cols>"
    : "";
  const rows = [sheet.header, ...sheet.rows]
    .map((row, r) => {
      const style = r === 0 ? ' s="1"' : "";
      const cells = row
        .map((v, c) => {
          const ref = colName(c) + String(r + 1);
          if (v === null || v === undefined || v === "") return "";
          if (typeof v === "number" && Number.isFinite(v)) return `<c r="${ref}"${style}><v>${String(v)}</v></c>`;
          return `<c r="${ref}"${style} t="inlineStr"><is><t xml:space="preserve">${esc(String(v))}</t></is></c>`;
        })
        .join("");
      return `<row r="${String(r + 1)}">${cells}</row>`;
    })
    .join("");
  // ตรึงแถวหัวไว้ตอนเลื่อนลง
  const views =
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>';
  return XML_HEAD + `<worksheet xmlns="${NS_MAIN}">${views}${cols}<sheetData>${rows}</sheetData></worksheet>`;
}

function colName(i: number): string {
  let s = "";
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

function esc(s: string): string {
  // ตัด control char ที่ XML 1.0 ไม่รับ (ไม่งั้น Excel บอกว่าไฟล์เสีย)
  return s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "").replace(/[<>&"]/g, (ch) => {
    switch (ch) {
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case "&":
        return "&amp;";
      default:
        return "&quot;";
    }
  });
}

function uniqueSheetNames(raw: string[]): string[] {
  const seen = new Set<string>();
  return raw.map((name, i) => {
    let base = name.replace(/[\\/?*[\]:]/g, " ").trim().slice(0, 31) || `Sheet${String(i + 1)}`;
    let n = 2;
    while (seen.has(base.toLowerCase())) base = `${base.slice(0, 28)} ${String(n++)}`;
    seen.add(base.toLowerCase());
    return base;
  });
}

// ---------------------------------------------------------------- zip (stored)

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (const b of data) c = (CRC_TABLE[(c ^ b) & 0xff] ?? 0) ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function zipStored(files: { path: string; data: Uint8Array }[]): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const f of files) {
    const name = enc.encode(f.path);
    const crc = crc32(f.data);
    const size = f.data.length;

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0x0800, true); // ชื่อไฟล์เป็น UTF-8
    lv.setUint16(8, 0, true); // stored
    lv.setUint16(10, 0, true); // time
    lv.setUint16(12, 0x21, true); // date 1980-01-01
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);
    lv.setUint32(22, size, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);

    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 0x21, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    central.set(name, 46);

    locals.push(local, f.data);
    centrals.push(central);
    offset += local.length + size;
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  const parts = [...locals, ...centrals, end];
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let pos = 0;
  for (const p of parts) {
    out.set(p, pos);
    pos += p.length;
  }
  return out;
}
